<script setup lang="ts">
/**
 * Readonly book-position preview for a manuscript map leaf (#266).
 * Frames use the project print size. Spread = one map across two facing pages.
 */
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
  type Component,
  type Ref,
} from "vue";
import { storeToRefs } from "pinia";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import {
  DEFAULT_PRINT_SIZE,
  findMapById,
  type ZwMapFigure,
  type ZwPrintSize,
} from "../lib/zwConfig";
import { mapPlacementFit } from "../lib/mapPlacementFit";
import { parseMapDocument, type MapDocument } from "../lib/mapDocument";
import { mapToSvgDataUrl } from "../lib/mapToSvg";
import { joinProjectPath, readProjectFile } from "../lib/tauri/core";
import {
  listProjectAssets,
  readProjectAssetDataUrl,
} from "../lib/tauri/assets";
import {
  cssLengthToPx,
  printPageSizeFor,
  printPageSizePixels,
} from "../export/printSizes";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { useRouter } from "vue-router";
import { displayFileName } from "../lib/fileNames";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuItem from "./ui/ZwMenuItem.vue";
import {
  IconExtentBleed,
  IconExtentSafe,
  IconExtentText,
  IconFitContain,
  IconFitCover,
  IconEditMap,
  IconPageSingle,
  IconPageSpread,
  IconRotateCcw,
  IconRotateCw,
  IconTrash,
} from "./ribbonIcons";

/** Body text margin for print (0.75in), matching native PDF. */
const TEXT_MARGIN_IN = 0.75;
/** KDP / Lulu safety margin for live content (~0.5in from trim). */
const SAFE_MARGIN_IN = 0.5;

type PlacementOption<T extends string> = {
  readonly value: T;
  readonly label: string;
  readonly icon: Component;
};

const SPREAD_OPTIONS: readonly PlacementOption<ZwMapFigure["spread"]>[] = [
  { value: "single", label: "One page", icon: IconPageSingle },
  { value: "spread", label: "Two-page spread", icon: IconPageSpread },
];

const EXTENT_OPTIONS: readonly PlacementOption<ZwMapFigure["printExtent"]>[] = [
  {
    value: "textMargins",
    label: "Text margins",
    icon: IconExtentText,
  },
  {
    value: "safeZone",
    label: "Safe zone",
    icon: IconExtentSafe,
  },
  {
    value: "fullBleed",
    label: "Full bleed",
    icon: IconExtentBleed,
  },
];

const FIT_OPTIONS: readonly PlacementOption<ZwMapFigure["objectFit"]>[] = [
  { value: "contain", label: "Contain", icon: IconFitContain },
  { value: "cover", label: "Cover", icon: IconFitCover },
];

const props = defineProps<{
  mapId: string;
}>();

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const router = useRouter();
const { manuscript, rootPath, projectConfig } = storeToRefs(session);

const figure = computed<Maybe<ZwMapFigure>>(() =>
  findMapById(manuscript.value, props.mapId),
);

const printSizeId = computed<ZwPrintSize>(() => {
  if (isNothing(projectConfig.value)) return DEFAULT_PRINT_SIZE;
  return projectConfig.value.value.printSize;
});

const pageSize = computed(() => printPageSizeFor(printSizeId.value));
const pagePx = computed(() => printPageSizePixels(pageSize.value));

const isSpread = computed(
  () => isJust(figure.value) && figure.value.value.spread === "spread",
);

const printExtent = computed<ZwMapFigure["printExtent"]>(() =>
  isJust(figure.value) ? figure.value.value.printExtent : "safeZone",
);

const objectFit = computed<ZwMapFigure["objectFit"]>(() =>
  isJust(figure.value) ? figure.value.value.objectFit : "contain",
);

const spreadOption = computed(
  () =>
    SPREAD_OPTIONS.find(
      (o) => o.value === (isSpread.value ? "spread" : "single"),
    ) ?? SPREAD_OPTIONS[0]!,
);

const extentOption = computed(
  () =>
    EXTENT_OPTIONS.find((o) => o.value === printExtent.value) ??
    EXTENT_OPTIONS[1]!,
);

const fitOption = computed(
  () => FIT_OPTIONS.find((o) => o.value === objectFit.value) ?? FIT_OPTIONS[0]!,
);

/** Outer sheet aspect: one page, or two pages side by side. */
const sheetAspect = computed(() => {
  const { width, height } = pagePx.value;
  const w = isSpread.value ? width * 2 : width;
  return `${w} / ${height}`;
});

/**
 * Fit the print sheet into the preview body: prefer height, shrink width so
 * the aspect ratio of the chosen print size stays exact.
 */
const sheetStyle = computed(() => {
  const { width, height } = pagePx.value;
  const sheetW = isSpread.value ? width * 2 : width;
  const ar = sheetW / height;
  return {
    aspectRatio: sheetAspect.value,
    // Cap by viewport height, then derive width from print aspect.
    width: `min(100%, calc(min(70vh, 40rem) * ${ar}))`,
    maxHeight: "min(70vh, 40rem)",
  };
});

/**
 * Map inset from trim, as % of page. Graphic pages use safeZone / fullBleed,
 * not body text margins (Vellum Print Extent model).
 */
const mapSlotStyle = computed(() => {
  if (printExtent.value === "fullBleed") {
    return { inset: "0" };
  }
  const pageWIn = cssLengthToPx(pageSize.value.width) / 96;
  const pageHIn = cssLengthToPx(pageSize.value.height) / 96;
  const marginIn =
    printExtent.value === "textMargins" ? TEXT_MARGIN_IN : SAFE_MARGIN_IN;
  const topBottom = `${(marginIn / pageHIn) * 100}%`;
  const leftRight = `${(marginIn / pageWIn) * 100}%`;
  if (isSpread.value) {
    // Outer edges keep the margin; the map crosses the spine.
    return { inset: `${topBottom} ${leftRight}` };
  }
  return { inset: `${topBottom} ${leftRight}` };
});

const orientationDeg = computed(() =>
  isJust(figure.value) ? figure.value.value.orientation : 0,
);

const loadError: Ref<Maybe<string>> = ref(nothing);
const svgSrc: Ref<Maybe<string>> = ref(nothing);
const busy = ref(false);

/** Slot size in CSS px (ResizeObserver). */
const slotSize = ref({ width: 0, height: 0 });
/** Intrinsic map size before orientation. */
const naturalSize = ref({ width: 0, height: 0 });
const mapSlotEl = useTemplateRef<HTMLElement>("mapSlot");
const mapImgEl = useTemplateRef<HTMLImageElement>("mapImg");
let slotObserver: Maybe<ResizeObserver> = nothing;

/**
 * Post-rotation fit: a frame with the *visual* size is centered in the slot.
 * The img keeps unrotated layout size and is rotated inside that frame, so
 * grid centering is not skewed by CSS transform (which does not affect layout).
 */
const placementFit = computed(() => {
  const slotW = slotSize.value.width;
  const slotH = slotSize.value.height;
  const natW = naturalSize.value.width;
  const natH = naturalSize.value.height;
  if (slotW < 1 || slotH < 1 || natW < 1 || natH < 1) return nothing;
  return just(
    mapPlacementFit(
      slotW,
      slotH,
      natW,
      natH,
      orientationDeg.value,
      objectFit.value,
    ),
  );
});

const mapFitFrameStyle = computed(() => {
  if (isNothing(placementFit.value)) return { opacity: "0" };
  const fit = placementFit.value.value;
  return {
    width: `${fit.visualWidth}px`,
    height: `${fit.visualHeight}px`,
    opacity: "1",
  };
});

const mapImgStyle = computed(() => {
  if (isNothing(placementFit.value)) return { opacity: "0" };
  const fit = placementFit.value.value;
  const style: Record<string, string> = {
    width: `${fit.layoutWidth}px`,
    height: `${fit.layoutHeight}px`,
    maxWidth: "none",
    maxHeight: "none",
    opacity: "1",
  };
  if (orientationDeg.value !== 0) {
    style.transform = `translate(-50%, -50%) rotate(${orientationDeg.value}deg)`;
  } else {
    style.transform = "translate(-50%, -50%)";
  }
  return style;
});

function readNaturalSize(img: HTMLImageElement): void {
  if (img.naturalWidth < 1 || img.naturalHeight < 1) return;
  naturalSize.value = {
    width: img.naturalWidth,
    height: img.naturalHeight,
  };
}

function onMapImgLoad(event: Event): void {
  const img = event.target;
  if (img instanceof HTMLImageElement === false) return;
  readNaturalSize(img);
}

function bindSlotObserver(el: Maybe<HTMLElement>): void {
  if (isJust(slotObserver)) {
    slotObserver.value.disconnect();
  }
  slotObserver = nothing;
  if (isNothing(el)) {
    slotSize.value = { width: 0, height: 0 };
    return;
  }
  const apply = (width: number, height: number): void => {
    slotSize.value = { width, height };
  };
  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (typeof entry === "undefined") return;
    apply(entry.contentRect.width, entry.contentRect.height);
  });
  observer.observe(el.value);
  slotObserver = just(observer);
  const rect = el.value.getBoundingClientRect();
  apply(rect.width, rect.height);
}

async function loadPreview(): Promise<void> {
  if (isNothing(figure.value) || isNothing(rootPath.value)) {
    svgSrc.value = nothing;
    return;
  }
  busy.value = true;
  loadError.value = nothing;
  try {
    const root = rootPath.value.value;
    const mapPath = figure.value.value.mapPath;
    const raw = await readProjectFile(joinProjectPath(root, mapPath));
    const doc: MapDocument = parseMapDocument(raw);
    const assets = new Map<string, string>();
    try {
      const listed = await listProjectAssets(root);
      await Promise.all(
        listed.map(async (path) => {
          try {
            assets.set(path, await readProjectAssetDataUrl(root, path));
          } catch {
            // Skip missing assets.
          }
        }),
      );
    } catch {
      // Assets optional for preview.
    }
    if (typeof doc.landmass !== "undefined") {
      try {
        assets.set(
          doc.landmass.path,
          await readProjectAssetDataUrl(root, doc.landmass.path),
        );
      } catch {
        // Landmass optional.
      }
    }
    naturalSize.value = { width: 0, height: 0 };
    svgSrc.value = just(
      mapToSvgDataUrl(doc, { bounds: "page", assetDataUrls: assets }),
    );
  } catch (error) {
    loadError.value = just(
      error instanceof Error ? error.message : "Could not load map",
    );
    svgSrc.value = nothing;
    naturalSize.value = { width: 0, height: 0 };
  } finally {
    busy.value = false;
  }
}

onMounted(() => {
  void loadPreview();
});
onUnmounted(() => {
  if (isJust(slotObserver)) {
    slotObserver.value.disconnect();
  }
  slotObserver = nothing;
});
watch(
  () => [props.mapId, figure.value] as const,
  () => {
    void loadPreview();
  },
);
watch(
  () => mapSlotEl.value,
  (el) => {
    bindSlotObserver(toMaybe(el));
  },
);
watch(svgSrc, async () => {
  await nextTick();
  const imgM = toMaybe(mapImgEl.value);
  if (isJust(imgM) && imgM.value.complete === true) {
    readNaturalSize(imgM.value);
  }
});

function nextOrientation(
  current: ZwMapFigure["orientation"],
  step: 90 | -90,
): ZwMapFigure["orientation"] {
  const next = (((current + step) % 360) + 360) % 360;
  if (next === 90 || next === 180 || next === 270) return next;
  return 0;
}

async function rotate(step: 90 | -90): Promise<void> {
  if (isNothing(figure.value)) return;
  const orientation = nextOrientation(figure.value.value.orientation, step);
  await workspace.onUpdateMapFigure(props.mapId, {
    ...figure.value.value,
    orientation,
  });
}

async function setSpread(spread: ZwMapFigure["spread"]): Promise<void> {
  if (isNothing(figure.value)) return;
  await workspace.onUpdateMapFigure(props.mapId, {
    ...figure.value.value,
    spread,
  });
}

async function setPrintExtent(
  printExtentNext: ZwMapFigure["printExtent"],
): Promise<void> {
  if (isNothing(figure.value)) return;
  await workspace.onUpdateMapFigure(props.mapId, {
    ...figure.value.value,
    printExtent: printExtentNext,
  });
}

async function setObjectFit(
  objectFitNext: ZwMapFigure["objectFit"],
): Promise<void> {
  if (isNothing(figure.value)) return;
  await workspace.onUpdateMapFigure(props.mapId, {
    ...figure.value.value,
    objectFit: objectFitNext,
  });
}

async function removeFromManuscript(): Promise<void> {
  await workspace.onRemoveMapFromManuscript(props.mapId);
}

function openInMaps(): void {
  if (isNothing(figure.value)) return;
  const name = displayFileName(figure.value.value.mapPath);
  void router.push({
    name: "database-map",
    params: { name: name.endsWith(".zwmap") ? name : `${name}.zwmap` },
  });
}
</script>

<template>
  <div class="manuscript-map-preview">
    <header class="manuscript-map-preview-header">
      <div class="manuscript-map-preview-titles">
        <h1 class="manuscript-map-preview-title">
          {{ isJust(figure) ? figure.value.title : "Map" }}
        </h1>
      </div>
      <div
        v-if="isJust(figure)"
        class="manuscript-map-preview-actions"
        role="toolbar"
        aria-label="Map placement"
      >
        <button
          type="button"
          class="manuscript-map-preview-icon-btn"
          title="Rotate counterclockwise"
          aria-label="Rotate counterclockwise"
          @click="rotate(-90)"
        >
          <IconRotateCcw class="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="manuscript-map-preview-icon-btn"
          title="Rotate clockwise"
          aria-label="Rotate clockwise"
          @click="rotate(90)"
        >
          <IconRotateCw class="size-4" aria-hidden="true" />
        </button>

        <ZwMenu align="start" :menu-label="just('Page layout')">
          <template #trigger="{ toggle, attrs, open }">
            <button
              type="button"
              class="manuscript-map-preview-menu-btn"
              :class="{ 'manuscript-map-preview-menu-btn-open': open }"
              :title="`Page layout: ${spreadOption.label}`"
              :aria-label="`Page layout: ${spreadOption.label}`"
              v-bind="attrs"
              @click="toggle()"
            >
              <component
                :is="spreadOption.icon"
                class="size-4"
                aria-hidden="true"
              />
              <span class="manuscript-map-preview-menu-label">{{
                spreadOption.label
              }}</span>
              <svg
                class="manuscript-map-preview-menu-caret"
                width="8"
                height="8"
                viewBox="0 0 8 8"
                aria-hidden="true"
              >
                <path
                  d="M1 2.5 4 5.5 7 2.5"
                  fill="none"
                  stroke="currentColor"
                />
              </svg>
            </button>
          </template>
          <ZwMenuItem
            v-for="option in SPREAD_OPTIONS"
            :key="option.value"
            :label="option.label"
            :icon="option.icon"
            :shortcut="
              figure.value.spread === option.value ? just('✓') : nothing
            "
            :title="nothing"
            @select="void setSpread(option.value)"
          />
        </ZwMenu>

        <ZwMenu align="start" :menu-label="just('Print extent')">
          <template #trigger="{ toggle, attrs, open }">
            <button
              type="button"
              class="manuscript-map-preview-menu-btn"
              :class="{ 'manuscript-map-preview-menu-btn-open': open }"
              :title="`Print extent: ${extentOption.label}`"
              :aria-label="`Print extent: ${extentOption.label}`"
              v-bind="attrs"
              @click="toggle()"
            >
              <component
                :is="extentOption.icon"
                class="size-4"
                aria-hidden="true"
              />
              <span class="manuscript-map-preview-menu-label">{{
                extentOption.label
              }}</span>
              <svg
                class="manuscript-map-preview-menu-caret"
                width="8"
                height="8"
                viewBox="0 0 8 8"
                aria-hidden="true"
              >
                <path
                  d="M1 2.5 4 5.5 7 2.5"
                  fill="none"
                  stroke="currentColor"
                />
              </svg>
            </button>
          </template>
          <ZwMenuItem
            v-for="option in EXTENT_OPTIONS"
            :key="option.value"
            :label="option.label"
            :icon="option.icon"
            :shortcut="
              figure.value.printExtent === option.value ? just('✓') : nothing
            "
            :title="nothing"
            @select="void setPrintExtent(option.value)"
          />
        </ZwMenu>

        <ZwMenu align="start" :menu-label="just('Map fit')">
          <template #trigger="{ toggle, attrs, open }">
            <button
              type="button"
              class="manuscript-map-preview-menu-btn"
              :class="{ 'manuscript-map-preview-menu-btn-open': open }"
              :title="`Map fit: ${fitOption.label}`"
              :aria-label="`Map fit: ${fitOption.label}`"
              v-bind="attrs"
              @click="toggle()"
            >
              <component
                :is="fitOption.icon"
                class="size-4"
                aria-hidden="true"
              />
              <span class="manuscript-map-preview-menu-label">{{
                fitOption.label
              }}</span>
              <svg
                class="manuscript-map-preview-menu-caret"
                width="8"
                height="8"
                viewBox="0 0 8 8"
                aria-hidden="true"
              >
                <path
                  d="M1 2.5 4 5.5 7 2.5"
                  fill="none"
                  stroke="currentColor"
                />
              </svg>
            </button>
          </template>
          <ZwMenuItem
            v-for="option in FIT_OPTIONS"
            :key="option.value"
            :label="option.label"
            :icon="option.icon"
            :shortcut="
              figure.value.objectFit === option.value ? just('✓') : nothing
            "
            :title="nothing"
            @select="void setObjectFit(option.value)"
          />
        </ZwMenu>

        <button
          type="button"
          class="manuscript-map-preview-icon-btn"
          title="Edit map"
          aria-label="Edit map"
          @click="openInMaps"
        >
          <IconEditMap class="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="manuscript-map-preview-icon-btn"
          title="Remove from book"
          aria-label="Remove from book"
          @click="removeFromManuscript"
        >
          <IconTrash class="size-4" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div class="manuscript-map-preview-body">
      <p v-if="busy" class="manuscript-map-preview-status">Loading map…</p>
      <p v-else-if="isJust(loadError)" class="manuscript-map-preview-status">
        {{ loadError.value }}
      </p>
      <p v-else-if="isNothing(figure)" class="manuscript-map-preview-status">
        This map is no longer in the manuscript.
      </p>
      <div
        v-else
        class="manuscript-map-preview-sheet"
        :class="{ 'manuscript-map-preview-sheet-spread': isSpread }"
        :style="sheetStyle"
        :aria-label="
          isSpread
            ? `Map on a ${pageSize.label} two-page spread`
            : `Map on a ${pageSize.label} page`
        "
      >
        <div
          v-if="isSpread"
          class="manuscript-map-preview-pages"
          aria-hidden="true"
        >
          <div class="manuscript-map-preview-page" />
          <div class="manuscript-map-preview-spine" />
          <div class="manuscript-map-preview-page" />
        </div>
        <div v-else class="manuscript-map-preview-page" aria-hidden="true" />

        <div
          ref="mapSlot"
          class="manuscript-map-preview-map-slot"
          :style="mapSlotStyle"
        >
          <div
            v-if="isJust(svgSrc)"
            class="manuscript-map-preview-fit-frame"
            :style="mapFitFrameStyle"
          >
            <img
              ref="mapImg"
              class="manuscript-map-preview-img"
              :src="svgSrc.value"
              :alt="figure.value.title"
              :style="mapImgStyle"
              @load="onMapImgLoad"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.manuscript-map-preview {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--bg);
  color: var(--text);
}

.manuscript-map-preview-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-raised);
}

.manuscript-map-preview-titles {
  min-width: 0;
}

.manuscript-map-preview-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.manuscript-map-preview-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
  align-items: center;
}

.manuscript-map-preview-icon-btn {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  margin: 0;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  background: transparent;
  color: var(--text);
  cursor: pointer;
}

.manuscript-map-preview-icon-btn:hover {
  background: var(--hover-fill);
}

.manuscript-map-preview-icon-btn:focus-visible {
  outline: 2px solid color-mix(in oklch, var(--color-zw-amber) 55%, transparent);
  outline-offset: 1px;
}

.manuscript-map-preview-menu-btn {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.85rem;
  margin: 0;
  padding: 0 0.4rem 0 0.45rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  color: var(--text);
  font-family: var(--font-ui);
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
}

.manuscript-map-preview-menu-btn:hover,
.manuscript-map-preview-menu-btn-open {
  background: var(--hover-fill);
}

.manuscript-map-preview-menu-btn:focus-visible {
  outline: 2px solid color-mix(in oklch, var(--color-zw-amber) 55%, transparent);
  outline-offset: 1px;
}

.manuscript-map-preview-menu-label {
  font-family: inherit;
  font-weight: 400;
}

.manuscript-map-preview-menu-caret {
  flex-shrink: 0;
  opacity: 0.7;
}

.manuscript-map-preview-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: var(--bg);
}

.manuscript-map-preview-status {
  margin: 2rem auto;
  color: var(--text-muted);
}

/* Print sheet: exact book page (or two-page) aspect ratio. */
.manuscript-map-preview-sheet {
  position: relative;
  flex: 0 0 auto;
  height: auto;
  container-type: size;
  background: transparent;
}

.manuscript-map-preview-pages {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  pointer-events: none;
}

.manuscript-map-preview-page {
  position: absolute;
  inset: 0;
  border: 1px solid var(--border);
  background: #f7f4ef;
  box-sizing: border-box;
}

.manuscript-map-preview-sheet-spread > .manuscript-map-preview-page {
  display: none;
}

.manuscript-map-preview-pages .manuscript-map-preview-page {
  position: relative;
  inset: auto;
}

.manuscript-map-preview-spine {
  width: 0.4rem;
  align-self: stretch;
  background: color-mix(in oklch, var(--bg) 55%, #2a241c);
  border-left: 1px solid color-mix(in oklch, var(--border) 70%, #000);
  border-right: 1px solid color-mix(in oklch, var(--border) 70%, #000);
  z-index: 2;
}

/* Inset comes from printExtent (textMargins / safeZone / fullBleed). */
.manuscript-map-preview-map-slot {
  position: absolute;
  display: grid;
  place-items: center;
  overflow: hidden;
  z-index: 1;
  container-type: size;
}

/* Visual (post-rotation) box; grid centers this, not the unrotated img. */
.manuscript-map-preview-fit-frame {
  position: relative;
  flex: 0 0 auto;
  overflow: visible;
}

.manuscript-map-preview-img {
  position: absolute;
  left: 50%;
  top: 50%;
  display: block;
  /* Width/height from mapPlacementFit; translate+rotate keep paint in the frame. */
  transform-origin: center center;
}
</style>
